import * as React from 'react';
import {
  styled,
  useTheme,
  Theme,
  CSSObject,
} from '@mui/material/styles';
import { Link } from 'react-router-dom';
import {
  Box,
  Drawer as MuiDrawer,
  AppBar as MuiAppBar,
  AppBarProps as MuiAppBarProps,
  Toolbar,
  List,
  CssBaseline,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Menu,
  Mail as MailIcon,
  ExpandMore,
} from '@mui/icons-material';
import appRoutes from '../../routes/appRoutes';
import { RouteType } from '../../routes/config';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  height: '64px',
  justifyContent: 'center',
  backgroundColor: '#2c3e50',
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': {
        ...openedMixin(theme),
        backgroundColor: '#2c3e50',
      },
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': {
        ...closedMixin(theme),
        backgroundColor: '#2c3e50',
      },
    }),
  }),
);

const CollapsableList = (route: RouteType, index: number, drawerOpen: boolean) => {
  const [open, setOpen] = React.useState(false);
  const handleClick = () => setOpen(!open);

  // Collapse the list when drawer is closed
  React.useEffect(() => {
    if (!drawerOpen) {
      setOpen(false);
    }
  }, [drawerOpen]);

  return (
  <ListItem sx={{
    padding: 0,
    color: '#ecf0f1',
    display: 'block'
  }} key={index}>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <ListItemButton 
        component={Link}
        to={route.path}
        sx={{
          flex: 1,
          '&:hover': {
            backgroundColor: '#34495e',
          }
        }}
      >
        <ListItemIcon sx={{color: '#ecf0f1'}}>
          {route.sidebarProps?.icon}
        </ListItemIcon>
        <ListItemText primary={route.sidebarProps?.displayText} sx={{ opacity: drawerOpen ? 1 : 0 }} />
      </ListItemButton>
      <IconButton
        onClick={handleClick}
        sx={{
          color: '#ecf0f1',
          opacity: drawerOpen ? 1 : 0,
          width: drawerOpen ? 'auto' : 0,
          minWidth: 0,
          padding: drawerOpen ? 1 : 0,
          marginRight: drawerOpen ? 1 : 0,
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: '#34495e',
          }
        }}
      >
        <ExpandMore sx={{ 
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.3s'
        }} />
      </IconButton>
    </Box>
    <Collapse in={open} timeout="auto" unmountOnExit style={{alignContent: 'center'}}>
      <List component="div" disablePadding >
      {route.child?.map((child, index) => (
        child.sidebarProps ? Item(child, index, true) : null
      ))}
      </List>
    </Collapse>
  </ListItem>
  );
}

const Item = (route: RouteType, index: number, child: boolean = false) => {
  const [open] = React.useState(true);
  return (
    <ListItem
      key={route.sidebarProps?.displayText}
      disablePadding
      sx={{
        display: 'block',
        padding: 0,
        color: '#ecf0f1',
      }}
    >
      <ListItemButton
        component={Link}
        to={route.path}
        sx={{
          minHeight: 48,
          justifyContent: open ? 'initial' : 'center',
          px: 2.5,
          '&:hover': {
            backgroundColor: '#34495e',
          }
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: open ? 3 : 'auto',
            justifyContent: 'center',
            color: '#ecf0f1',
          }}
        >
          {route.sidebarProps?.icon}
        </ListItemIcon>
        <ListItemText primary={route.sidebarProps?.displayText} sx={{ opacity: open ? 1 : 0 }} />
      </ListItemButton>
    </ListItem>
  );
}

export default function MiniDrawer() {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color={'inherit'}
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 5,
              ...(open && { display: 'none' }),
            }}
          >
            <Menu />
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div"
            sx={{
              fontWeight: 600,
              letterSpacing: '0.5px'
            }}
          >
            Philip Payne
          </Typography>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <DrawerHeader sx={{ backgroundColor: '#2c3e50' }}>
          <IconButton onClick={handleDrawerClose} sx={{color: '#ecf0f1'}}>
            {theme.direction === 'rtl' ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </DrawerHeader>
        <Divider sx={{ backgroundColor: '#34495e' }} />
        <List sx={{ backgroundColor: '#2c3e50', flexGrow: 1 }}>
          {appRoutes.map((route, index) => (
            route.child
              ? CollapsableList(route, index, open)
              : Item(route, index)
          ))}
          <Divider sx={{ backgroundColor: '#34495e', my: 2 }} />
        </List>
        <List sx={{ backgroundColor: '#2c3e50' }}>
          <ListItem key='contact' disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                color: '#ecf0f1',
                '&:hover': {
                  backgroundColor: '#34495e',
                }
              }}
              component="a"
              href="mailto:philip.payne40@gmail.com"
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                  color: '#ecf0f1'
                }}
              >
                <MailIcon />
              </ListItemIcon>
              <ListItemText primary='Get in touch' sx={{ opacity: open ? 1 : 0 }} />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
    </Box>
  );
}